import "./EditDamagedQuantity.css";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import Search from "../../../components/dms_common/Search";
import {
  setDisableEditing,
  setSaleReturnRows,
  setSelectedRowToEdit
} from "../../../stateManagement/actions/claims";
import WestIcon from "@mui/icons-material/West";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { BatchNumberObj, ReasonDetail, SaleReturnRow, BatchEditorProps } from "../utils/CreateClaimUtils";
import CustomCsPcInput from "../../../components/dms_common/CustomCsPcInput";
import RenderSvg from "../../../components/renderSvg/RenderSvg";
import { calculateTotals, calculateTotalCsAndPcAndOther } from "../utils/utils";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Typography, Box, Accordion, AccordionSummary,AccordionDetails } from '@mui/material';
import { openPopup } from "../../../stateManagement/actions/popupActions";
import { logAnalyticsEvent } from "../../../../src/firebase";

interface Batches {
  [returnOrderNo: string]: {
    batchIds: BatchNumberObj[];
  };
}

interface BatchIdMap {
  [returnOrderNo: string]: {
    [batchId: string]: boolean;
  };
}

// Components
const BatchListItem = React.memo(({
  batch,
  isSelected,
  onSelect,
  isChanged,
  caseUnitLabel,
  otherUnitLabel,
  packUnitLabel,
}: {
  batch: any;
  isSelected: boolean;
  onSelect: () => void;
  isChanged: boolean;
  caseUnitLabel: string;
  otherUnitLabel: string;
  packUnitLabel: string;
}) => {
  const { caseQty, otherQty, pieceQty } = batch;

  return (
    <div
      className={`batchContainer ${isSelected && "batch-selected"}`}
      onClick={onSelect}
    >
      <span className={`${isSelected && "batch-selected-text"}`} style={{display:"flex",alignItems:"center",gap:"6px"}}>
      {batch?.batchId}{" "}
        {isChanged && (
          <CheckCircleIcon
            style={{
              color: "#00C6B1",
              fontSize: "14px",
            }}
          />
        )}
      </span>
      <div>
        {caseUnitLabel && (
          <CustomCsPcInput
            value={caseQty}
            onChange={() => null}
            label={caseUnitLabel}
            percentage={95}
            borderColor={isSelected ? "#00C6B1" : "#d9d9d9"}
            borderWidth="0.5px"
            readOnly={true}
          />
        )}
        {otherUnitLabel && (
          <CustomCsPcInput
            value={otherQty}
            onChange={() => null}
            label={otherUnitLabel}
            percentage={95}
            borderColor={isSelected ? "#00C6B1" : "#d9d9d9"}
            borderWidth="0.5px"
            readOnly={true}
          />
        )}
        {packUnitLabel && (
          <CustomCsPcInput
            value={pieceQty}
            onChange={() => null}
            label={packUnitLabel}
            percentage={95}
            borderColor={isSelected ? "#00C6B1" : "#d9d9d9"}
            borderWidth="0.5px"
            readOnly={true}
          />
        )}
      </div>
    </div>
  );
});

const BatchEditor: React.FC<BatchEditorProps> = ({
  batch,
  originalBatch,
  onChange,
  onReset,
  onSave,
  caseUnitLabel,
  otherUnitLabel,
  packUnitLabel,
}) => {
  const { disableEditing } = useSelector((state: any) => state.claims);
  const validateInput = (value: string) => { return !value.startsWith("-"); };
  return (
    <div className="edit-right">
      <div className="edit-right-top">
        <div className="edit-right-top-header">
          <p className="batch-id-nos">{batch?.batchId}</p>
        </div>
        <div className="batch-ids-container">
          <div className="reasons-parent">
            <div className="reasons-container">
              {batch.reasonDetails.map((reason, index) => (
                <div className="sale-label-input-container-edit" key={index}>
                   <p className="sale-label-csps-edit" title={reason.reason}>{reason.reason}</p>
                  <div className="sale-label-input-parent-edit">
                    <div className="sale-cs-pc-container-edit">
                      {caseUnitLabel && (
                        <CustomCsPcInput
                          value={reason.caseQty}
                          onChange={(e) => {
                            if (!disableEditing && validateInput(e.target.value)) {
                              onChange(index, "caseQty", e.target.value);
                            }
                          }}
                          label={caseUnitLabel}
                          percentage={100}
                          readOnly={disableEditing}
                        />
                      )}
                      {otherUnitLabel && (
                        <CustomCsPcInput
                          value={reason.otherQty}
                          onChange={(e) => {
                            if (!disableEditing && validateInput(e.target.value)) {
                              onChange(index, "otherQty", e.target.value);
                            }
                          }}
                          label={otherUnitLabel}
                          percentage={100}
                          readOnly={disableEditing}
                        />
                      )}
                      {packUnitLabel && (
                        <CustomCsPcInput
                          value={reason.pieceQty}
                          onChange={(e) => {
                            if (!disableEditing && validateInput(e.target.value)) {
                              onChange(index, "pieceQty", e.target.value);
                            }
                          }}
                          label={packUnitLabel}
                          percentage={100}
                          readOnly={disableEditing}
                        />
                      )}
                    </div>
                    {!disableEditing && (
                      <div style={{ marginTop: "6px", cursor: "pointer" }} onClick={() => onReset(index)}>
                        <RenderSvg name='undoIcon' />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="save-reasons-button">
            <button
                className={`button-save-edit ${disableEditing ? "button-disabled" : ""}`} 
                onClick={onSave}
                disabled={disableEditing} 
              >
                <span className="button-save-text">Save</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EditDamagedQuantity: React.FC = () => {
  const [searchedValues, setSearchedValues] = useState<{ [returnOrderNo: string]: string }>({});
  const [expandedBatch, setExpandedBatch] = useState<any>({});
  const [expandedReturnOrderNo, setExpandedReturnOrderNo] = useState<string | null>(null);
  const [staticBatches, setStaticBatches] = useState<Batches>({});
  const [filteredStaticBatches, setFilteredStaticBatches] = useState<Batches>({});
  const [temporaryBatch, setTemporaryBatch] = useState<any>(null);
  const [batchIdMap, setBatchIdMap] = useState<BatchIdMap>({});
  const [modifiedReturnOrders, setModifiedReturnOrders] = useState<{ [returnOrderNo: string]: boolean }>({});
  const { selectedRowToEdit, saleReturnsRows, initialSaleReturnsRows} = useSelector(
    (state: any) => state.claims
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const { caseUnitLabel, otherUnitLabel, packUnitLabel, claimSubTypes} = ConfigDictionary;
  const { disableEditing } = useSelector((state: any) => state.claims);

  useEffect(() => {
    if (selectedRowToEdit?.returnOrderNo) {
      const initialBatches = selectedRowToEdit.returnOrderNo;
      setStaticBatches(initialBatches);
  
      const firstReturnOrderNo = Object.keys(initialBatches)[0];
      if (firstReturnOrderNo) {
        setExpandedReturnOrderNo(firstReturnOrderNo);
        const firstBatch = initialBatches[firstReturnOrderNo]?.batchIds[0];
        if (firstBatch) {
          setExpandedBatch(firstBatch);
        }
      }
    }
    return () => {
      dispatch(setDisableEditing(false));
    };
  }, [selectedRowToEdit]);
  useEffect(() => {
    const groupedFilteredBatches: any = {};
  
    Object.entries(staticBatches).forEach(([returnOrderNo, order]: [string, any]) => {
      const searchTerm = searchedValues[returnOrderNo] || "";
      groupedFilteredBatches[returnOrderNo] = {
        ...order,
        batchIds: order.batchIds.filter((batch: any) =>
          batch.batchId.toLocaleLowerCase().includes(searchTerm.toLocaleLowerCase())
        ),
      };
    });
  
    setFilteredStaticBatches(groupedFilteredBatches);
  }, [searchedValues, staticBatches]); 

const handleAccordionToggle = (returnOrderNo: string) => {
  setExpandedReturnOrderNo((prev) => (prev === returnOrderNo ? null : returnOrderNo));
  setExpandedBatch({});
  setTemporaryBatch(null);

  if (expandedReturnOrderNo !== returnOrderNo) {
    const firstBatch = staticBatches[returnOrderNo]?.batchIds[0];
    if (firstBatch) {
      setExpandedBatch(firstBatch);
    }
  }
};

  const handleSearchValueChange = (returnOrderNo: string, newValue: string) => {
    setSearchedValues((prev) => ({
      ...prev,
      [returnOrderNo]: newValue,
    }));
  };

  const handleCsPcCountChange = (
    index: number,
    key: keyof ReasonDetail,
    value: any,
    isReset: boolean = false
  ) => {
  
    if (!expandedReturnOrderNo) {
      return;
    }
  
    const batchToUpdate = JSON.parse(JSON.stringify(temporaryBatch || expandedBatch));
  
    if (isReset) {
      const originalBatch = staticBatches[expandedReturnOrderNo]?.batchIds?.find(
        (batch) => batch.batchId === batchToUpdate.batchId);

        if (originalBatch) {
          batchToUpdate.reasonDetails[index] = {
            ...batchToUpdate.reasonDetails[index],
            caseQty: originalBatch.reasonDetails[index].caseQty,
            pieceQty: originalBatch.reasonDetails[index].pieceQty,
            otherQty: originalBatch.reasonDetails[index].otherQty,
          };
        }
     } else {
      const numericValue = parseInt(value) || 0;
      const originalBatch = initialSaleReturnsRows
        .find((row: SaleReturnRow) => row.id === selectedRowToEdit?.id)
        ?.returnOrderNo[expandedReturnOrderNo]?.batchIds?.find(
          (batch: any) => batch.batchId === batchToUpdate.batchId
        );
  
      if (originalBatch) {
        const originalQty = originalBatch.reasonDetails[index][key];
        if (numericValue > originalQty) {
          openPopup("Alert", `The ${key} quantity cannot exceed the original quantity (${originalQty}).`);
          return;
        }
      }
      batchToUpdate.reasonDetails[index][key] = numericValue;
    }
  
    setTemporaryBatch(batchToUpdate);
  };
  
  const handleOnClose = (cancel = false) => {
    setTemporaryBatch(null);
    dispatch(setSelectedRowToEdit(null));
    setSearchedValues({});
    setExpandedBatch({});
    navigate(-1);
    if (cancel) {
      const hasModifications = Object.keys(modifiedReturnOrders).length > 0;

      if (hasModifications) {
        logAnalyticsEvent("claim_edit_cancelled", {
          distributor_id: JSON.parse(localStorage.authContext).user.loginId,
          claim_type: "Damage Returns",
          claim_subtype: "Sale Returns"
        });
      }
    }
  };

  const handleSave = () => {
    if (!temporaryBatch || !expandedReturnOrderNo) return;

    const { totalCaseQty, totalPieceQty, totalOtherQty } = calculateTotals([temporaryBatch]);
  
    const updatedBatch: BatchNumberObj = {
      ...temporaryBatch,
      caseQty: totalCaseQty,
      pieceQty: totalPieceQty,
      otherQty: totalOtherQty,
    };
    
    const updatedBatchesForGroup = staticBatches[expandedReturnOrderNo]?.batchIds.map((batch: BatchNumberObj) =>
      batch.batchId === updatedBatch.batchId ? updatedBatch : batch
    );
  
    const updatedStaticBatches: Batches = {
      ...staticBatches,
      [expandedReturnOrderNo]: {
        ...staticBatches[expandedReturnOrderNo],
        batchIds: updatedBatchesForGroup,
      },
    };
    
    setStaticBatches(updatedStaticBatches);
    setFilteredStaticBatches(updatedStaticBatches);
  
    setBatchIdMap((prevMap: BatchIdMap) => ({
      ...prevMap,
      [expandedReturnOrderNo]: {
        ...(prevMap[expandedReturnOrderNo] || {}),
        [updatedBatch.batchId]: true,
      },
    }));
    setModifiedReturnOrders((prev) => ({
      ...prev,
      [expandedReturnOrderNo]: true,
    }));
    setTemporaryBatch(null);
    setExpandedBatch({});
  };
  
  const areAllBatchesZero = (batches: Batches): boolean => {
    return Object.values(batches).every((returnOrder) =>
      returnOrder.batchIds.every((batch) =>
        batch.reasonDetails.every(
          (reason) =>
            reason.caseQty === 0 && reason.pieceQty === 0 && reason.otherQty === 0
        )
      )
    );
  };

  const handleSubmit = () => {
    if (!filteredStaticBatches || Object.keys(filteredStaticBatches).length === 0) {
      openPopup("Alert", "No changes to submit.");
      return;
    }
  
    if (!selectedRowToEdit) {
      console.error("selectedRowToEdit is null");
      return;
    }

    if (areAllBatchesZero(filteredStaticBatches)) {
      openPopup("Alert", "All the batch IDs cannot be set to 0.");
      return;
    }

    let totalCaseQty = 0;
    let totalPieceQty = 0;
    let totalOtherQty = 0;
  
    const updatedReturnOrders = Object.entries(filteredStaticBatches).reduce(
      (acc, [returnOrderNo, returnOrder]) => {
        const { totalCs, totalPc, totalOther } = calculateTotalCsAndPcAndOther(returnOrder.batchIds);
        if (totalCs > 0 || totalPc > 0 || totalOther > 0) {
          acc[returnOrderNo] = returnOrder;
          totalCaseQty += totalCs;
          totalPieceQty += totalPc;
          totalOtherQty += totalOther;
        }
        return acc;
      },
      {} as Batches
    );
  
      const { ptdCs, ptdPc, ptdOther } = selectedRowToEdit;
      const totalClaimAmount = ptdCs * totalCaseQty + ptdPc * totalPieceQty + ptdOther * totalOtherQty;
    
      const updatedSelectedRow = {
        ...selectedRowToEdit,
        returnOrderNo: updatedReturnOrders,
        cases: totalCaseQty,
        pieces: totalPieceQty,
        others: totalOtherQty,
        totalClaimAmount, 
      };
    
  
    const updatedSaleReturnRows = saleReturnsRows.map((row: any) =>
      row.id === selectedRowToEdit?.id ? updatedSelectedRow : row
    );
  
    dispatch(setSaleReturnRows(updatedSaleReturnRows));
    const hasModifications = Object.keys(modifiedReturnOrders).length > 0;

    if (hasModifications) {
    logAnalyticsEvent("claim_edit_saved", {
      distributor_id: JSON.parse(localStorage.authContext).user.loginId,
      claim_type: "Damage Returns",
      claim_subtype: "Sale Returns"
    });
  }
    handleOnClose();
  };

const handleChangeExpandedBatch = React.useCallback((batch: BatchNumberObj) => {
  setExpandedBatch((prevState: BatchNumberObj | null) =>
    prevState?.batchId === batch.batchId ? null : batch
  );
  setTemporaryBatch(null);
}, []);

  const handleReset = () => {
    setStaticBatches(selectedRowToEdit?.returnOrderNo || {});
    setFilteredStaticBatches(selectedRowToEdit?.returnOrderNo || {});
    setTemporaryBatch(null);
    setExpandedBatch({});
    setExpandedReturnOrderNo(null);
    setModifiedReturnOrders({});
    if (expandedReturnOrderNo) {
      setBatchIdMap((prevMap) => ({
        ...prevMap,
        [expandedReturnOrderNo]: {}, 
      }));
    }
    const hasModifications = Object.keys(modifiedReturnOrders).length > 0;

    if (hasModifications) {
      logAnalyticsEvent("claim_edit_cancelled", {
        distributor_id: JSON.parse(localStorage.authContext).user.loginId,
        claim_type: "Damage Returns",
        claim_subtype: "Sale Returns"
      });
    }    
  };
  return (
    <div className="edit-claim-container">
      <header>
        <div>
          <WestIcon
            style={{ cursor: "pointer" }}
            onClick={() => { handleOnClose(true) }}
          />
          <p>
            {claimSubTypes[0]} - Edit damage quantity for{" "}
            {selectedRowToEdit
              ? `${selectedRowToEdit.skuName} (${selectedRowToEdit.skuCode})`
              : "N/A"}
          </p>
        </div>
        <CloseIcon style={{ cursor: "pointer" }} onClick={() => { handleOnClose(true) }} />
      </header>

      {/* Modal Content */}
      <div className="edit-claim-modal-container">
        <div className="edit-claim-accordion">
          <div className="left-top-heading">
            <span>{Object.keys(filteredStaticBatches).length} Results</span>

          </div>
          {Object.keys(filteredStaticBatches).map((returnOrderNo) => (
            <Accordion
              key={returnOrderNo}
              expanded={expandedReturnOrderNo === returnOrderNo}
              onChange={() => handleAccordionToggle(returnOrderNo)}
              sx={{
                "& .MuiAccordionSummary-root": {
                  backgroundColor:
                    expandedReturnOrderNo === returnOrderNo
                      ? "#FAFFFF !important"
                      : "#FFFFFF !important",
                },
                width: "100%",
                minHeight: "80px",
                borderRadius: "10px",
                border: "1.5px solid",
                borderColor:
                  expandedReturnOrderNo === returnOrderNo
                    ? "#7EE3D8"
                    : "#DEDEDE",
                boxShadow: "0 2px 2px rgba(0, 0, 0, 0.1)",
                marginBottom: "14px",
                transition: "all 0.3s ease"
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMore />}
                sx={{
                  backgroundColor:
                    expandedReturnOrderNo === returnOrderNo
                      ? "#7EE3D8"
                      : "#FAFFFF",
                }}
              >
                <Box>
                  <div className="left-top-heading">
                    Return Order No. {returnOrderNo}&nbsp;
                    {modifiedReturnOrders[returnOrderNo] && (
                      <CheckCircleIcon
                        style={{
                          color: "#00C6B1",
                          fontSize: "14px",
                        }}
                      />
                    )}
                  </div>
                  <Typography variant="caption" color="text.secondary">
                    {filteredStaticBatches[returnOrderNo]?.batchIds?.length}{" "}
                    Batch IDs
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails
                sx={{
                  backgroundColor:
                    expandedReturnOrderNo === returnOrderNo ? "#FAFFFF" : "white",
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                }}
              >
                <div style={{ width: "260px" }}>
                  <Search
                    searchFunction={(newValue) => handleSearchValueChange(returnOrderNo, newValue)}
                    label="Search Batch ID"
                  />
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "30px",
                    width: "100%",
                  }}
                >
                  <div className="batches-list-container" style={{ width: "50%", flexShrink: 0 }}>
                    {filteredStaticBatches[returnOrderNo]?.batchIds?.length ? (
                      <>
                        <div className="batch-list-heading">
                          <span>Select a batch ID to edit approved damage quantity</span>
                        </div>
                        <div className="batches-list">
                          {filteredStaticBatches[returnOrderNo].batchIds.map((batch: any) => {
                            return (
                              <BatchListItem
                                key={batch.batchId}
                                batch={batch}
                                isSelected={expandedBatch?.batchId === batch.batchId}
                                onSelect={() => handleChangeExpandedBatch(batch)}
                                isChanged={
                                  expandedReturnOrderNo && batchIdMap[expandedReturnOrderNo]
                                    ? !!batchIdMap[expandedReturnOrderNo][batch.batchId]
                                    : false
                                }
                                caseUnitLabel={caseUnitLabel}
                                otherUnitLabel={selectedRowToEdit?.otherUnitName || otherUnitLabel}
                                packUnitLabel={packUnitLabel}
                              />
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div className="batch-list-heading">
                        <span>No data found.</span>
                      </div>
                    )}
                  </div>

                  {/* Batch Editor */}
                  {expandedBatch?.batchId && expandedReturnOrderNo && (
                    filteredStaticBatches[expandedReturnOrderNo]?.batchIds?.length > 0 && (
                      <div style={{ width: "50%", flexShrink: 0 }}>
                        <BatchEditor
                          batch={temporaryBatch || expandedBatch}
                          originalBatch={staticBatches[expandedReturnOrderNo]?.batchIds?.find(
                            (b) => b.batchId === expandedBatch.batchId
                          )}
                          onChange={(index, key, value) => {
                            handleCsPcCountChange(index, key, value);
                          }}
                          onReset={(index) => handleCsPcCountChange(index, "caseQty", "", true)}
                          onSave={handleSave}
                          caseUnitLabel={caseUnitLabel}
                          otherUnitLabel={selectedRowToEdit?.otherUnitName || otherUnitLabel}
                          packUnitLabel={packUnitLabel}
                        />
                      </div>
                    )
                  )}
                </div>
              </AccordionDetails>
            </Accordion>
          ))}
        </div>
      </div>

      <footer>
        <button
          className={`button-reset-edit ${disableEditing ? "button-disabled" : ""}`}
          onClick={handleReset}
          disabled={disableEditing}
        >
          <span className="button-reset-text-edit">Reset</span>
        </button>
        <button
          className="button-save"
          onClick={() => {
            handleSubmit();
          }}
        >
          <span className="button-save-text">
            {disableEditing ? "Close" : "Submit"}
          </span>
        </button>
      </footer>
    </div>
  );
};

export default EditDamagedQuantity;